import { Form, FormCancel } from "../components";
import { useFeedback } from "../hooks/useFeedback";
import { Toast, PageTitle, Btn, Card, Input, Modal, Spinner } from "../components";
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";

const emptyEditForm = () => ({
  name: "",
  price_cents: 0,
  active: true,
  features: {},
  limits: {},
});

const formatCurrency = (cents) => (
  Number(cents || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
);

const FeatureToggle = ({ item, enabled, onChange }) => (
  <div style={{
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 14,
    background: enabled ? "rgba(34,197,94,0.06)" : "var(--s2)",
  }}>
    <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
      <input
        type="checkbox"
        checked={Boolean(enabled)}
        onChange={(e) => onChange(item.key, e.target.checked)}
        style={{ marginTop: 3 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.label}</div>
        <div style={{ color: "var(--muted)", fontSize: '0.9375rem', lineHeight: 1.5 }}>{item.description}</div>
        {item.pages?.length > 0 && (
          <div style={{ marginTop: 8, fontSize: '0.875rem', color: "var(--muted)" }}>
            <strong style={{ color: "var(--text)" }}>Telas:</strong> {item.pages.join(" · ")}
          </div>
        )}
        {item.automations?.length > 0 && (
          <div style={{ marginTop: 4, fontSize: '0.875rem', color: "var(--muted)" }}>
            <strong style={{ color: "var(--text)" }}>n8n / automação:</strong> {item.automations.join(" · ")}
          </div>
        )}
        {item.api?.length > 0 && (
          <div style={{ marginTop: 4, fontSize: '0.875rem', color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
            {item.api.join(" · ")}
          </div>
        )}
      </div>
    </label>
  </div>
);

export default function PlanosPage() {
  const [plans, setPlans] = useState([]);
  const [catalog, setCatalog] = useState({ features: [], limits: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [form, setForm] = useState(emptyEditForm);
  const { toast, toastType, showToast, showError, dismissToast } = useFeedback("planos");



  const loadData = async () => {
    setLoading(true);
    try {
      const [plansData, catalogData] = await Promise.all([
        apiFetch("/plans?all=1"),
        apiFetch("/plans/catalog"),
      ]);
      setPlans(plansData);
      setCatalog(catalogData);
    } catch (e) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const featureCatalog = catalog.features || [];
  const limitCatalog = catalog.limits || [];

  const openEdit = (plan) => {
    setEditPlan(plan);
    setForm({
      name: plan.name || "",
      price_cents: Number(plan.price_cents || 0),
      active: plan.active !== false,
      features: { ...(plan.features || {}) },
      limits: { ...(plan.limits || {}) },
    });
    setModal(true);
  };

  const setFeature = (key, enabled) => {
    setForm((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: enabled },
    }));
  };

  const setLimit = (key, value) => {
    setForm((prev) => ({
      ...prev,
      limits: { ...prev.limits, [key]: value },
    }));
  };

  const save = async () => {
    if (!editPlan) return;
    if (!form.name.trim()) {
      showError("Nome do plano é obrigatório.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        price_cents: Number(form.price_cents || 0),
        active: form.active,
        features: featureCatalog.reduce((acc, item) => ({
          ...acc,
          [item.key]: form.features[item.key] === true,
        }), {}),
        limits: limitCatalog.reduce((acc, item) => {
          const raw = form.limits[item.key];
          acc[item.key] = raw === "" || raw == null ? null : Number.parseInt(raw, 10);
          return acc;
        }, {}),
      };
      const updated = await apiFetch(`/plans/${editPlan.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      setPlans((prev) => prev.map((plan) => (plan.id === updated.id ? { ...plan, ...updated } : plan)));
      setModal(false);
      showToast(`Plano ${updated.name} atualizado. Os usuários precisam entrar novamente para acessar os recursos atualizados.`);
    } catch (e) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const matrixHeaders = useMemo(
    () => featureCatalog.map((item) => item.label),
    [featureCatalog]
  );

  return (
    <div className="fade-in" style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <PageTitle icon="card">Planos e recursos</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', maxWidth: 720, lineHeight: 1.5 }}>
            Defina os recursos e limites disponíveis para as transportadoras de cada plano.
            As alterações se aplicam a todas as transportadoras do plano quando os usuários entrarem novamente.
          </p>
        </div>
        <Btn variant="secondary" onClick={loadData} disabled={loading}>Atualizar</Btn>
      </div>

      {toast && !(modal && toastType === "error") && <Toast tone={toastType} onDismiss={dismissToast}>{toast}</Toast>}

      <Card style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Plano", "Preço", "Clientes", ...matrixHeaders, ""].map((header) => (
                <th key={header || "actions"} style={{
                  padding: "12px 14px",
                  textAlign: "left",
                  fontSize: '0.8125rem',
                  color: "var(--muted)",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  whiteSpace: "nowrap",
                }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={matrixHeaders.length + 4} style={{ padding: 40, textAlign: "center" }}><Spinner /></td></tr>
            )}
            {!loading && plans.map((plan, index) => (
              <tr
                key={plan.id}
                style={{
                  borderBottom: index < plans.length - 1 ? "1px solid var(--border)" : "none",
                  opacity: plan.active ? 1 : 0.55,
                }}
              >
                <td style={{ padding: "12px 14px" }}>
                  <div style={{ fontWeight: 700 }}>{plan.name}</div>
                  <div style={{ color: "var(--muted)", fontSize: '0.875rem', fontFamily: "var(--font-mono)" }}>{plan.code}</div>
                </td>
                <td style={{ padding: "12px 14px", fontSize: '0.9375rem' }}>{formatCurrency(plan.price_cents)}</td>
                <td style={{ padding: "12px 14px", fontFamily: "var(--font-mono)", color: "var(--accent)" }}>
                  {plan.clientes_ativos ?? 0}
                </td>
                {featureCatalog.map((item) => (
                  <td key={item.key} style={{ padding: "12px 14px", textAlign: "center" }}>
                    <span style={{ color: plan.features?.[item.key] ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
                      {plan.features?.[item.key] ? "✓" : "✗"}
                    </span>
                  </td>
                ))}
                <td style={{ padding: "12px 14px" }}>
                  <Btn size="sm" variant="secondary" onClick={() => openEdit(plan)}>Editar</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card style={{ padding: 18, display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 700 }}>Como funciona</div>
        <ul style={{ margin: 0, paddingLeft: 18, color: "var(--muted)", fontSize: '0.9375rem', lineHeight: 1.7 }}>
          <li><strong style={{ color: "var(--text)" }}>Telas</strong> somem do menu quando o recurso está desligado no plano.</li>
          <li><strong style={{ color: "var(--text)" }}>API</strong> bloqueia ações mesmo se alguém tentar acessar direto.</li>
          <li><strong style={{ color: "var(--text)" }}>n8n</strong> continua por cliente (webhooks e Evolution no cadastro do cliente); o plano controla se aquelas automações podem rodar.</li>
          <li>Para mudar o plano de um cliente específico, use <strong>Clientes RotaFlex</strong>.</li>
        </ul>
      </Card>

      {modal && editPlan && (
        <Modal title={`Editar plano — ${editPlan.code}`} onClose={() => !saving && setModal(false)}>
          {toast && toastType === "error" && <Toast tone="error" inline onDismiss={dismissToast}>{toast}</Toast>}
          <Form values={form} onSubmit={save} busy={saving} style={{ display: "grid", gap: 16 }}>
            <Input
              label="Nome exibido *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Input
              label="Preço mensal (centavos)"
              type="number"
              min="0"
              value={form.price_cents}
              onChange={(e) => setForm({ ...form, price_cents: e.target.value })}
              placeholder="0"
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: '0.9375rem' }}>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Plano ativo para novos clientes
            </label>

            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, display: "grid", gap: 10 }}>
              <div style={{ fontWeight: 700 }}>Recursos incluídos</div>
              {featureCatalog.map((item) => (
                <FeatureToggle
                  key={item.key}
                  item={item}
                  enabled={form.features[item.key]}
                  onChange={setFeature}
                />
              ))}
            </div>

            {limitCatalog.length > 0 && (
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, display: "grid", gap: 10 }}>
                <div style={{ fontWeight: 700 }}>Limites</div>
                {limitCatalog.map((item) => (
                  <div key={item.key}>
                    <Input
                      label={item.label}
                      type="number"
                      min="0"
                      value={form.limits[item.key] ?? ""}
                      onChange={(e) => setLimit(item.key, e.target.value)}
                      placeholder="Ilimitado"
                    />
                    <div style={{ color: "var(--muted)", fontSize: '0.875rem', marginTop: 4 }}>{item.description}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="rf-form-actions">
              <FormCancel disabled={saving} />
              <Btn type="submit" disabled={saving}>{saving ? "Salvando..." : "Salvar plano"}</Btn>
            </div>
          </Form>
        </Modal>
      )}
    </div>
  );
}
